/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import Bio from 'src/components/PageTitle/Bio'
import Text from 'src/components/ts-components/Text'
import { PageTitle } from '../steps/Main'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import {
  NEW_IDENTITY_ID,
  buildIdentityOptions,
  findIdentityPerson,
  hasIdentityStats,
  identityLabel,
  identityPersonId,
  identitySubtitle,
  type IdentityOptionItem,
  type IdentityPerson,
} from '../utils/identityOptions'

const PLAYER_PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

export interface IdentitySectionProps {
  /**
   * The people this reader can register — their family when registering a
   * player, their team officials when registering a team. The caller picks the
   * list; this only renders it.
   */
  people: IdentityPerson[] | null | undefined
  /** Registering a team rather than a player, which changes the wording. */
  isTeamRole: boolean
  /** The person currently chosen, or the create-new sentinel. */
  selected: IdentityPerson | null | undefined
  /** Passed the chosen person, or null for "create new". */
  onSelect: (person: IdentityPerson | null) => void
  disabled?: boolean
  /**
   * `half` of the page (default), or `full` width of a column the section
   * already sits in — a team rep's left column beside the team summary.
   */
  fieldWidth?: 'half' | 'full'
  /** The rule beneath the field; off when the surrounding layout draws one. */
  showDivider?: boolean
}

const createNewLabel = (isTeamRole: boolean): string =>
  isTeamRole ? 'Create a New Team Official' : 'Create a New Player'

/**
 * The text a typed query is matched against for one option.
 *
 * The create-new row keeps its own label; everyone else matches on the
 * searchable string `buildIdentityOptions` built, which carries the email too.
 */
const searchTextFor = (
  option: { value?: unknown } | undefined,
  people: IdentityPerson[] | null | undefined
): string => {
  const value = String(option?.value ?? '')
  if (!value || value === NEW_IDENTITY_ID) {
    return value === NEW_IDENTITY_ID ? 'create new' : ''
  }
  const person = findIdentityPerson(people, value)
  return person ? `${identityLabel(person)} ${identitySubtitle(person)}` : ''
}

/** Holds the stats line for a person with none, so every row keeps its height. */
const NO_STATS_LABEL = 'No stats added'

/**
 * One row: avatar, then name, stats and email.
 *
 * The stat line is the shared `Bio` — the same age/height/weight icon row the
 * player header uses — rather than a second implementation of it here. A person
 * without stats gets a muted placeholder on that line instead, in the same text
 * preset, so rows stay one height and the email always sits on the third line.
 *
 * Team officials skip the stat line: age, height and weight describe a player,
 * not a team official, so their rows are just name and email.
 */
const renderPersonRow = (person: IdentityPerson, showStats: boolean) => {
  const subtitle = identitySubtitle(person)
  return (
    <div css={rowStyle}>
      <img
        src={getImageThumbnailId(person) || PLAYER_PLACEHOLDER}
        alt=''
        css={avatarStyle}
        onError={(e) => {
          const img = e.target as HTMLImageElement
          img.onerror = null
          img.src = PLAYER_PLACEHOLDER
        }}
      />
      <span css={rowTextStyle}>
        <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
          {identityLabel(person)}
        </Text>
        {showStats &&
          (hasIdentityStats(person) ? (
            // Bio's own top margin is sized for a page header; inside a
            // dropdown row the lines sit together.
            <span css={statsStyle}>
              <Bio
                birthDate={person.birth_date}
                height={person.height}
                weight={person.weight}
              />
            </span>
          ) : (
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_100}
            >
              {NO_STATS_LABEL}
            </Text>
          ))}
        {subtitle ? (
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {subtitle}
          </Text>
        ) : null}
      </span>
    </div>
  )
}

/**
 * Who is being registered, asked beside the form that describes them.
 *
 * This used to be a whole screen ahead of the form, which meant choosing a
 * person before seeing any of what registering them involved. Here, choosing
 * someone prefills the form's reserved fields directly below, so the choice and
 * its consequence are visible together.
 *
 * A dropdown rather than the start step's card grid: it sits above a long form
 * as one field among several, and a grid of cards there would read as the
 * page's subject rather than one of its inputs.
 */
const IdentitySection = ({
  people,
  isTeamRole,
  selected,
  onSelect,
  disabled = false,
  fieldWidth = 'half',
  showDivider = true,
}: IdentitySectionProps) => {
  // Name, stats (players only) and email per row, so two people sharing a name
  // are distinguishable. Matches the roster add-player search.
  // `label` stays the plain searchable string underneath; only the display is
  // replaced, so antd still matches a typed email against it.
  const options = buildIdentityOptions(people, isTeamRole).map(
    (option: IdentityOptionItem) => ({
      ...option,
      label: option.person ? (
        renderPersonRow(option.person, !isTeamRole)
      ) : (
        // The create-new row is an action, not a person — colored to read as
        // one rather than as another name in the list.
        <Text variant='paragraphSmall' weight='semiBold' color={colors.PRIMARY}>
          {option.label}
        </Text>
      ),
    })
  )

  // A chosen person shows their own id; the create-new sentinel shows itself.
  // Empty means nothing chosen yet, which renders the placeholder.
  const value = selected ? identityPersonId(selected) ?? NEW_IDENTITY_ID : ''

  return (
    <div css={containerStyle}>
      {/* The shared heading the old select-role screen used, so this section
          reads as the same question it always was rather than a new one. It
          carries the rule beneath the subtitle too. */}
      <PageTitle
        title={
          isTeamRole
            ? 'Choose One of Your Team Officials'
            : 'Select One of Your Players'
        }
        subtitle={
          isTeamRole
            ? 'Choose the team official that you want to register or create a new one.'
            : 'Choose the player that you want to register or create a new one.'
        }
        showAlert={false}
        showSuccess={false}
        // The rule goes below the field instead, so it closes off the whole
        // question — heading, subtitle and answer — rather than separating the
        // heading from the field it introduces.
        showDivider={false}
      />

      <div css={[fieldStyle, fieldWidth === 'full' && fullWidthStyle]}>
        <SelectInput
          label=''
          name='identity'
          value={value}
          disabled={disabled}
          options={options}
          fieldErrors={[]}
          customError=''
          showSearch
          // Match against the option's own searchable text, not its rendered
          // node — the default matcher would stringify the JSX and find
          // nothing. `buildIdentityOptions` puts name AND email in there.
          filterOption={(input, option) =>
            searchTextFor(option, people)
              .toLowerCase()
              .includes(input.trim().toLowerCase())
          }
          popupMatchSelectWidth={false}
          // The chosen row collapses to the name alone; the three-line form
          // belongs in the list, not in the closed field.
          labelRender={({ value: selectedValue }) => {
            const person = findIdentityPerson(people, String(selectedValue))
            return person ? identityLabel(person) : createNewLabel(isTeamRole)
          }}
          placeholder={capitalizeWords(
            isTeamRole ? 'Search Team Officials' : 'Search Players'
          )}
          onChange={(next) =>
            // The sentinel resolves to null: there is no person yet, and the
            // form below collects their details instead.
            onSelect(findIdentityPerson(people, next))
          }
        />
      </div>

      {showDivider ? <div css={dividerStyle} /> : null}
    </div>
  )
}

export default IdentitySection

/* Closes the section off beneath the field, matching PageTitle's own rule —
   the same token, so the two are indistinguishable where both appear.

   32px, not 24px: PageTitle's fixed 24px bottom margin carries the subtitle's
   line-height leading with it, so an equal 24px here reads as less space than
   the gap above the field. The extra makes the field sit optically centred
   between the subtitle and the rule. */
const dividerStyle = css`
  width: 100%;
  height: 1px;
  margin-top: 32px;
  background-color: ${colors.VERY_DARK_GRAYISH_BLUE};
`

/* PageTitle brings its own vertical margins, so this only stacks. */
const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

/* The avatar sits at the top, level with the name, rather than centred on the
   three lines beside it. */
const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  padding: 4px 0;
`

/* 24px against a ~20px name line: drops 2px to sit on the name, not above it —
   the same offset as the team picker's logo. */
const avatarStyle = css`
  margin-top: 2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex: none;
  object-fit: cover;
`

/* Bio is a page-header component and sets `margin-top: 16px`; a dropdown row
   has no room for that. Its gaps and icon sizing are left alone. */
const statsStyle = css`
  > div {
    margin-top: 0;
    gap: 12px;
  }
`

/* The lines stack; without this they would become two more items in the
   row's own flex line. */
const rowTextStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

/* Half width, matching the team select in `TeamInfoSection` directly below —
   the two are the same kind of field and should not disagree on size. Full
   width on mobile, where half a column is unusable. */
const fieldStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const fullWidthStyle = css`
  width: 100%;
`
